import * as React from 'react';
import Table from 'react-bootstrap/Table';
import Stack from '@mui/material/Stack';
import Button from '@mui/material/Button';
import VisibilityOutlinedIcon from '@mui/icons-material/VisibilityOutlined';
import PersonAddOutlinedIcon from '@mui/icons-material/PersonAddOutlined';
import Pagination from '@mui/material/Pagination';
import { NavLink } from 'react-router-dom';
import Col from 'react-bootstrap/Col';
import Form from 'react-bootstrap/Form';
import Row from 'react-bootstrap/Row';
import { postRequest } from '../../lib/api';
import { AuthContext } from '../../authContext/context';
import { useContext, useEffect, useState, useRef } from 'react';
import CircularProgress from '@mui/material/CircularProgress';
import BreadcrumbsWrapper from '../../components/breadcrumbs';
import { checkPermission } from '../../utils/checkPermissions';

export default function Partners() {
  const [userList, setUserList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalDocs, setTotalDocs] = useState(0);
  const [searchName, setSearchName] = useState('');

  const authCtx = useContext(AuthContext);
  const userPermissions = authCtx.permissions;
  const canManagePartners = checkPermission(userPermissions, 'settings', 'manage_partners');
  const debounceRef = useRef();

  const fetchPartners = async (searchNameVal, pageVal = page) => {
    setLoading(true);
    try {
      const payload = {
        page: pageVal,
        limit: 10,
        title: searchNameVal,
      };

      const response = await postRequest('private/partnerList', payload, authCtx.token);
      if (response?.status === 200 && response?.data) {
        setUserList(response.data.docs || []);
        setTotalPages(response.data.totalPages || 1);
        setTotalDocs(response.data.totalDocs || 0);
      } else {
        setUserList([]);
        setTotalPages(1);
        setTotalDocs(0);
      }
    } catch (err) {
      setUserList([]);
      setTotalPages(1);
      setTotalDocs(0);
    }
    setLoading(false);
  };

  // Fetch on mount and when page changes
  useEffect(() => {
    fetchPartners(searchName, page);
    // eslint-disable-next-line
  }, [page, authCtx.token]);

  // Debounced search on name change
  useEffect(() => {
    setPage(1); // Reset to first page on search
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      fetchPartners(searchName, 1);
    }, 400);
    // eslint-disable-next-line
  }, [searchName]);

  const handlePageChange = (event, value) => {
    setPage(value);
  };

  return (
    <>
      <BreadcrumbsWrapper
        items={[
          { label: 'Dashboard', path: '/dashboard' },
          { label: 'Partners', path: '' },
        ]}
      />
      <br></br>
      <div className='page-header'>
        <h1 className='mt-0'>Partners</h1>
        {canManagePartners && (
          <div className='header-right-btn'>
            <Stack spacing={2} direction="row">
              <Button variant="outlined" className='red-outline-btn text-capitalize'>
                <NavLink to="/partner/create" className="red-link-text">
                  <PersonAddOutlinedIcon /> &nbsp; Add New Partner
                </NavLink>
              </Button>
            </Stack>
          </div>
        )}
      </div>
      <div className='page-top-formview mb-3'>
        <Form>
          <Row className="mb-3">
            <Form.Group as={Col} controlId="searchName">
              <Form.Label>Partner Name</Form.Label>
              <Form.Control
                type="text"
                placeholder="Enter partner name"
                value={searchName}
                onChange={e => {
                  setSearchName(e.target.value);
                  setPage(1);
                }}
              />
            </Form.Group>
          </Row>
        </Form>
      </div>

      <div>
        <Table striped responsive bordered>
          <thead>
            <tr>
              <th>Partner Name</th>
              <th>Total Users</th>
              <th>Action</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr>
                <td colSpan={3} style={{ textAlign: 'center' }}>
                  <CircularProgress size={28} />
                </td>
              </tr>
            ) : userList.length === 0 ? (
              <tr>
                <td colSpan={3} style={{ textAlign: 'center' }}>
                  No partners found.
                </td>
              </tr>
            ) : (
              userList.map((partner) => (
                <tr key={partner._id}>
                  <th>{partner?.name}</th>
                  <td>
                    <span className="badge bg-info">
                      {partner?.userCount ?? 0} {partner?.userCount === 1 ? 'User' : 'Users'}
                    </span>
                  </td>
                  <td>
                    <span
                      className='red-link-text cursor-pointer w-18'
                      title="View"
                      style={{ marginRight: 8 }}
                    >
                      <NavLink to={`/partner/view/${partner._id}`} className="red-link-text">
                        <VisibilityOutlinedIcon />
                      </NavLink>
                    </span>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </Table>
        {/* Pagination */}
        {userList.length > 0 && (
          <>
            <div style={{ display: 'flex', justifyContent: 'flex-start', marginTop: 8, marginBottom: 8 }}>
              <span className="text-muted small">
                Showing {((page - 1) * 10) + 1}-{Math.min(page * 10, totalDocs)} of {totalDocs} partners
              </span>
            </div>
            <div style={{ display: 'flex', justifyContent: 'center', marginTop: 8 }}>
              <Pagination
                count={totalPages}
                page={page}
                onChange={handlePageChange}
                color="primary"
                shape="rounded"
                size="medium"
                sx={{
                  '& .Mui-selected': {
                    background: 'linear-gradient(135deg, #764ba2 0%, #667eea 100%) !important',
                    color: '#fff !important',
                  },
                  '& .MuiPaginationItem-root': {
                    color: 'var(--bs-red-color)',
                    borderColor: 'var(--bs-red-color)',
                  },
                }}
              />
            </div>
          </>
        )}
      </div>
    </>
  );
}
